import { useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useSearchParams } from 'react-router-dom'
import { api } from '../api'
import type { Product } from '../types'
import { CardSkeleton, ProductCard } from '../components/ProductCard'

const titles: Record<string, string> = { women: 'Women', men: 'Men', new: 'New arrivals', sale: 'Sale', search: 'Search' }

export function Category() {
  const loc = useLocation()
  const [sp] = useSearchParams()
  // The route itself is the key: /women, /men, /new, /sale, /search
  const key = loc.pathname.replace(/^\//, '').split('/')[0] || 'women'
  const q = sp.get('q') ?? ''
  const [items, setItems] = useState<Product[] | null>(null)
  const [cat, setCat] = useState<string>('')
  const [size, setSize] = useState<string>('')
  const [onSale, setOnSale] = useState(false)
  const [sort, setSort] = useState('featured')
  const [open, setOpen] = useState(false)

  useEffect(() => {
    setItems(null); setCat('')
    const params: Record<string, string> = {}
    if (key === 'women' || key === 'men') params.department = key
    if (key === 'new') params.badge = 'new'
    if (key === 'sale') params.sale = '1'
    if (key === 'search') params.q = q
    api.products(params).then(r => setItems(r.products)).catch(() => setItems([]))
  }, [key, q])

  const cats = useMemo(() => Array.from(new Set((items ?? []).map(p => p.category))), [items])
  const shown = useMemo(() => {
    let out = (items ?? []).filter(p => (!cat || p.category === cat) && (!size || p.sizes.includes(size)) && (!onSale || !!p.compare_at_cents))
    if (sort === 'price-asc') out = [...out].sort((a, b) => a.price_cents - b.price_cents)
    if (sort === 'price-desc') out = [...out].sort((a, b) => b.price_cents - a.price_cents)
    return out
  }, [items, cat, size, onSale, sort])

  const title = key === 'search' ? `Results for “${q}”` : titles[key] ?? 'Shop'
  return (
    <div className="container">
      <div className="crumbs"><Link to="/">Home</Link> / {titles[key] ?? 'Shop'}</div>
      <div className="section-head" style={{ marginBottom: 12 }}>
        <h1 style={{ fontSize: 'clamp(28px,3.5vw,44px)' }}>{title}</h1>
      </div>
      <div className="listing">
        <aside className={'filters' + (open ? ' open' : '')}>
          <h3>Category</h3>
          <label><input type="radio" name="cat" checked={cat === ''} onChange={() => setCat('')} /> All</label>
          {cats.map(c => <label key={c}><input type="radio" name="cat" checked={cat === c} onChange={() => setCat(c)} /> {c[0].toUpperCase() + c.slice(1)}</label>)}
          <h3>Size</h3>
          <div className="sizes">{['XS', 'S', 'M', 'L', 'XL'].map(s => <button key={s} className={'pill' + (size === s ? ' on' : '')} onClick={() => setSize(size === s ? '' : s)}>{s}</button>)}</div>
          <h3>Offers</h3>
          <label><input type="checkbox" checked={onSale} onChange={e => setOnSale(e.target.checked)} /> On sale</label>
          {open && <button className="btn block" style={{ marginTop: 16 }} onClick={() => setOpen(false)}>Show {shown.length} items</button>}
        </aside>
        <div>
          <div className="toolbar">
            <span className="count">{items ? `${shown.length} items` : 'Loading…'}</span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn ghost filter-toggle" style={{ height: 36, padding: '0 14px' }} onClick={() => setOpen(true)}>Filters</button>
              <select className="select" value={sort} onChange={e => setSort(e.target.value)} aria-label="Sort">
                <option value="featured">Featured</option><option value="price-asc">Price: low to high</option><option value="price-desc">Price: high to low</option>
              </select>
            </div>
          </div>
          <div className="grid">
            {items ? shown.map(p => <ProductCard key={p.id} p={p} />) : Array.from({ length: 8 }).map((_, i) => <CardSkeleton key={i} />)}
          </div>
          {items && shown.length === 0 && <p style={{ color: 'var(--muted)', padding: '40px 0' }}>Nothing here yet — try another filter.</p>}
        </div>
      </div>
    </div>
  )
}
